/**
 * Plans v2 card 3.4 (owner decisions 16.10.2026): where an account's items may
 * live, asked by CAPABILITY (`cloud_sync`), never by plan name.
 *
 *   - Free and Basic (no `cloud_sync`): every new item is stored on this
 *     device. Items already in the cloud (old Free sync, or a plan that
 *     lapsed) are READ-ONLY: readable, downloadable, exportable — never edited
 *     and never deleted from here.
 *   - Pro and Team (`cloud_sync`): each workspace, board and document lives in
 *     the cloud or on this device, chosen per item; NEW items follow the
 *     default in Settings (`newItemLocationDefault`).
 *   - Team items (`team:<id>`) belong to the team and are not affected: a Free
 *     or Basic member of someone else's team keeps full access.
 *
 * Invoke-free and UI-free: records/itemLocation and records/recordsClient (both
 * in the MCP import graph) read it. The server's field names are read by
 * ./cloudState only.
 */
import { useEntitlementsStore } from '../store/entitlementsStore';
import { useAuthStore } from '../store/authStore';
import { isTauri } from '../utils/isTauri';
import { isBackendWorkspace } from '../utils/workspaceStorage';
import { isTeamWorkspace } from '../utils/workspaceShareEligibility';
import { cloudSyncFrom, readCloudRemoval } from './cloudState';

/** `{ allowed, known }`: may this account keep items in the cloud? */
export function cloudSync() {
  return cloudSyncFrom(useEntitlementsStore.getState());
}

/** Best answer now (the plan name's guess until the server has answered). */
export const hasCloudSync = () => cloudSync().allowed;

function signedInUserId() {
  const { isAuthenticated, user } = useAuthStore.getState?.() || {};
  return isAuthenticated && user?.id != null ? String(user.id) : null;
}

/** The server's word on the cloud copy (`{ flaggedAt, readOnly }`), from the last /auth/usage. */
export function cloudRemoval() {
  const user = useAuthStore.getState?.()?.user;
  return readCloudRemoval(user?._usage, user);
}

/**
 * Is the account's own cloud read-only (for writes from this app)? Only on
 * POSITIVE evidence — the server's `cloud.read_only`, or its capability answer
 * without `cloud_sync` (the server then refuses every personal cloud write,
 * data or not) — so a paying account is never locked out of its cloud items
 * while its plan is still loading, or by a backend that predates the flag.
 */
export function isCloudReadOnly() {
  if (!signedInUserId()) return false;
  if (cloudRemoval().readOnly === true) return true;
  const { allowed, known } = cloudSync();
  return known && !allowed;
}

/** `personal:<id>` of the signed-in account — its CLOUD scope. */
export function isOwnCloudScope(scopeKey) {
  const id = signedInUserId();
  return id != null && String(scopeKey || '') === `personal:${id}`;
}

/** Extensions whose items have a cloud/device location (item_location.rs LOCATED_EXTENSIONS). */
export const LOCATED_EXTENSIONS = Object.freeze(['documents', 'task-manager']);

/**
 * True when a write to `{ workspaceId, extensionId }` would change a board or
 * document in a read-only cloud. Desktop only: there the item can be
 * downloaded and edited on the device instead.
 */
export function isReadOnlyCloudWrite(args) {
  if (!isTauri()) return false;
  if (!LOCATED_EXTENSIONS.includes(args?.extensionId)) return false;
  return isOwnCloudScope(args?.workspaceId) && isCloudReadOnly();
}

/** The error a refused write rejects with. */
export const CLOUD_READ_ONLY = 'cloud_read_only';

/**
 * Is `workspace` an API workspace of the account's own READ-ONLY cloud? A
 * backend workspace (cloud REST or sync engine) that is not a team's and not
 * someone else's shared with this account, while the cloud is read-only
 * (`isCloudReadOnly`: a plan without `cloud_sync`). The server refuses every
 * write to it (backend #217: 402 `read_only`), so the app refuses first —
 * the plan's LOCAL caps are not what applies there. Deletes stay allowed.
 */
export function isReadOnlyCloudWorkspace(workspace) {
  const uid = signedInUserId();
  if (uid == null || !workspace || !isBackendWorkspace(workspace, true) || isTeamWorkspace(workspace)) return false;
  const ownerId = workspace.owner_id ?? workspace.user_id ?? workspace.owner?.id;
  const mine = workspace.role ? workspace.role === 'owner' : ownerId == null || String(ownerId) === uid;
  return mine && isCloudReadOnly();
}

/**
 * The location action an own board or top-level document offers, by where it
 * lives now ('cloud' | 'device'):
 *   'toCloud'  — Move to cloud (needs `cloud_sync`);
 *   'upgrade'  — the same entry without `cloud_sync`: opens the upgrade
 *                (card 3.3: a locked action is never a dead button);
 *   'toDevice' — Keep on this device: the item leaves the cloud (Pro / Team);
 *   'download' — Download to this device: a COPY, the cloud copy stays. Every
 *                cloud item of an account without `cloud_sync` gets this one,
 *                so nothing such an account does queues a cloud delete.
 */
export function locationActionFor(location) {
  if (location === 'device') return hasCloudSync() ? 'toCloud' : 'upgrade';
  return hasCloudSync() && !isCloudReadOnly() ? 'toDevice' : 'download';
}

// ── The Settings default for new items (Pro / Team) ──────────────────────────

export const NEW_ITEM_LOCATIONS = Object.freeze(['cloud', 'device']);
const defaultKey = (userId) => `apiboo.itemLocation.default.${userId}`;

/** 'cloud' (the default) or 'device', per account. */
export function newItemLocationDefault(userId = signedInUserId()) {
  if (userId == null) return 'cloud';
  try {
    return localStorage.getItem(defaultKey(userId)) === 'device' ? 'device' : 'cloud';
  } catch {
    return 'cloud';
  }
}

export function setNewItemLocationDefault(value, userId = signedInUserId()) {
  if (userId == null || !NEW_ITEM_LOCATIONS.includes(value)) return false;
  try {
    localStorage.setItem(defaultKey(userId), value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Should a NEW board, document or workspace go to this device? Yes without
 * `cloud_sync`; with it, when the Settings default says so.
 */
export function newItemsStayOnDevice() {
  if (!hasCloudSync()) return true;
  return newItemLocationDefault() === 'device';
}
